/*
 * Auto Timetable Generator
 * Automatically creates optimized timetables with no conflicts
 * Copyright (C) 2024
 */

import { clashMap } from './timetable-support.js';

/**
 * Normalize slot strings like "L41+L42" or "L41/L42" into an array of slot ids
 */
function parseSlotString(slotValue) {
    if (!slotValue) {
        return [];
    }

    if (Array.isArray(slotValue)) {
        return Array.from(
            new Set(
                slotValue.flatMap((value) => parseSlotString(value))
            )
        );
    }

    let normalized = slotValue.toString().trim().toUpperCase();

    if (!normalized || normalized === 'NIL') {
        return [];
    }

    // Replace common separators with +
    normalized = normalized.replace(/\s+/g, '');
    normalized = normalized.replace(/[\/]/g, '+');
    normalized = normalized.replace(/\++/g, '+');
    normalized = normalized.replace(/^\+|\+$/g, '');

    if (!normalized) {
        return [];
    }

    const tokens = normalized.split('+').map((token) => token.trim()).filter(Boolean);

    return Array.from(new Set(tokens));
}

/**
 * Configuration for auto-generation preferences
 */
const defaultPreferences = {
    preferMorning: false,          // Prefer morning slots (before lunch)
    preferEvening: false,          // Prefer evening slots (after lunch)
    minimizeGaps: true,            // Try to minimize gaps between classes
    avoidEarlyMorning: false,      // Avoid 8 AM classes
    avoidLateEvening: false,       // Avoid 6 PM+ classes
    maxConsecutive: 4,             // Max consecutive hours before break
    preferCompactDays: true,       // Try to compact classes into fewer days
    compactLabs: true,             // Try to have 2 labs on same day
    labsBefore7PM: false,          // Complete all labs before 7 PM
    teacherPreferences: {},        // {courseName: {green: [...], orange: [...], red: [...]}}
};

/**
 * Calculate the score for a slot combination
 * Higher score = better fit according to preferences
 */
function calculateScore(slots, preferences, schema, courseCode, faculty, isLab, selectedCourses) {
    let score = 100;
    
    // Parse slot times
    const slotTimes = slots.map(slot => getSlotTimeInfo(slot, schema));
    
    // Teacher preference scoring (HIGHEST PRIORITY)
    if (preferences.teacherPreferences && preferences.teacherPreferences[courseCode]) {
        const prefs = preferences.teacherPreferences[courseCode];
        if (prefs.green && prefs.green.includes(faculty)) {
            score += 1000; // Green - Prioritize highly
        } else if (prefs.orange && prefs.orange.includes(faculty)) {
            score += 100; // Orange - Second option
        } else if (prefs.red && prefs.red.includes(faculty)) {
            score -= 10000; // Red - Never take (effectively eliminates)
        }
    }
    
    // Lab-specific scoring
    if (isLab) {
        // Labs before 7 PM preference
        if (preferences.labsBefore7PM) {
            const after7Slots = slotTimes.filter(s => s && s.hour >= 19);
            if (after7Slots.length > 0) {
                score -= 500; // Heavy penalty for labs after 7 PM
            }
        }
        
        // Compact labs - bonus for labs on same day as other labs
        if (preferences.compactLabs && selectedCourses) {
            const labDays = getSlotDays(slots, schema);
            const otherLabDays = selectedCourses
                .filter(c => c.isLab && c.code !== courseCode)
                .flatMap(c => getSlotDays(c.slots, schema));
            
            const sharedDays = labDays.filter(day => otherLabDays.includes(day));
            score += sharedDays.length * 50; // Bonus for sharing days with other labs
        }
    }
    
    // Avoid early morning penalty
    if (preferences.avoidEarlyMorning) {
        const earlySlots = slotTimes.filter(s => s && s.hour < 9);
        score -= earlySlots.length * 15;
    }
    
    // Avoid late evening penalty
    if (preferences.avoidLateEvening) {
        const lateSlots = slotTimes.filter(s => s && s.hour >= 18);
        score -= lateSlots.length * 15;
    }
    
    // Prefer morning bonus
    if (preferences.preferMorning) {
        const morningSlots = slotTimes.filter(s => s && s.hour < 13);
        score += morningSlots.length * 10;
    }
    
    // Prefer evening bonus
    if (preferences.preferEvening) {
        const eveningSlots = slotTimes.filter(s => s && s.hour >= 14);
        score += eveningSlots.length * 10;
    }
    
    return score;
}

/**
 * Get time information for a slot
 */
function getSlotTimeInfo(slot, schema) {
    if (!schema || !slot) return null;
    
    // Search in theory slots
    for (const timeSlot of schema.theory || []) {
        if (timeSlot.days) {
            for (const day in timeSlot.days) {
                if (timeSlot.days[day] === slot) {
                    const hour = parseInt(timeSlot.start.split(':')[0]);
                    const isPM = timeSlot.start.includes('PM');
                    return {
                        hour: isPM && hour !== 12 ? hour + 12 : hour,
                        start: timeSlot.start,
                        end: timeSlot.end
                    };
                }
            }
        }
    }
    
    // Search in lab slots
    for (const timeSlot of schema.lab || []) {
        if (timeSlot.days) {
            for (const day in timeSlot.days) {
                if (timeSlot.days[day] === slot) {
                    const hour = parseInt(timeSlot.start.split(':')[0]);
                    const isPM = timeSlot.start.includes('PM');
                    return {
                        hour: isPM && hour !== 12 ? hour + 12 : hour,
                        start: timeSlot.start,
                        end: timeSlot.end
                    };
                }
            }
        }
    }
    
    return null;
}

/**
 * Get days of the week for given slots
 */
function getSlotDays(slots, schema) {
    const days = new Set();
    
    if (!schema || !slots) return [];
    
    slots.forEach(slot => {
        // Check theory slots
        for (const timeSlot of schema.theory || []) {
            if (timeSlot.days) {
                for (const day in timeSlot.days) {
                    if (timeSlot.days[day] === slot) {
                        days.add(day);
                    }
                }
            }
        }
        
        // Check lab slots
        for (const timeSlot of schema.lab || []) {
            if (timeSlot.days) {
                for (const day in timeSlot.days) {
                    if (timeSlot.days[day] === slot) {
                        days.add(day);
                    }
                }
            }
        }
    });
    
    return Array.from(days);
}

/**
 * Check if a course/slot is a lab
 */
function isLabCourse(slots) {
    if (!slots || slots.length === 0) return false;
    // Lab slots typically start with 'L'
    return slots.some(slot => slot.startsWith('L'));
}

/**
 * Check if two slot sets conflict with each other
 */
function hasConflict(slotsA, slotsB) {
    // Direct overlap check
    for (const slotA of slotsA) {
        if (slotsB.includes(slotA)) {
            return true;
        }
        
        // Check clashMap for indirect conflicts
        if (clashMap[slotA]) {
            for (const conflictSlot of clashMap[slotA]) {
                if (slotsB.includes(conflictSlot)) {
                    return true;
                }
            }
        }
    }
    
    return false;
}

/**
 * Get all available course options for a course code
 */
function getCourseOptions(courseCodeOrCourse, allData) {
    const options = [];
    
    // Handle both string code and course object
    let courseCode, courseName, originalCode;
    if (typeof courseCodeOrCourse === 'string') {
        courseCode = courseCodeOrCourse;
    } else {
        courseCode = courseCodeOrCourse.code;
        courseName = courseCodeOrCourse.courseName;
        originalCode = courseCodeOrCourse.originalCode;
    }
    
    // Check if this is a lab course (ends with "-Lab")
    const isLabCourse = courseCode.endsWith('-Lab');
    const baseCode = isLabCourse ? courseCode.replace(/-Lab$/, '') : courseCode;
    
    console.log(`[getCourseOptions] Looking for: "${courseCode}" (base: "${baseCode}", isLab: ${isLabCourse}${courseName ? `, courseName: "${courseName}"` : ''})`);
    
    let exactMatches = 0;
    let baseMatches = 0;
    let nameMatches = 0;
    
    for (const entry of allData) {
        let matches = false;
        
        if (isLabCourse) {
            // Priority 1: Exact CODE match (custom data with "-Lab" suffix)
            if (entry.CODE === courseCode) {
                matches = true;
                exactMatches++;
                console.log(`[getCourseOptions] ✓ Exact CODE match: ${entry.CODE} - ${entry.FACULTY} - ${entry.SLOT}`);
            }
            // Priority 2: Match by original courseName (if provided)
            else if (courseName && entry.COURSE_NAME === courseName) {
                matches = true;
                nameMatches++;
                console.log(`[getCourseOptions] ✓ CourseName match: "${courseName}" - ${entry.FACULTY} - ${entry.SLOT}`);
            }
            // Priority 3: Base code match and filter for lab courses
            else if (entry.CODE === baseCode || entry.ORIGINAL_CODE === baseCode) {
                // Check if this entry is a lab course:
                // - TYPE is 'LAB' or contains 'lab'
                // - Slots start with 'L' (lab slots)
                // - Title contains 'lab' (case-insensitive)
                const slots = parseSlotString(entry.SLOT);
                const hasLabSlots = slots.length > 0 && slots.every(slot => /^L\d+/.test(slot));
                const hasLabType = entry.TYPE && /lab/i.test(entry.TYPE);
                const hasLabTitle = entry.TITLE && /lab/i.test(entry.TITLE);
                
                if (hasLabSlots || hasLabType || hasLabTitle || entry.isLab) {
                    matches = true;
                    baseMatches++;
                    console.log(`[getCourseOptions] ✓ Base match (lab): ${entry.CODE} - ${entry.FACULTY} - ${entry.SLOT} (slots: ${slots.join(',')}, hasLabSlots: ${hasLabSlots})`);
                }
            }
        } else {
            // For theory courses, exact match only, but exclude lab entries
            // Also try courseName match for theory
            if (courseName && entry.COURSE_NAME === courseName && !entry.isLab && !entry.CODE.endsWith('-Lab')) {
                matches = true;
                nameMatches++;
                console.log(`[getCourseOptions] ✓ CourseName match (theory): "${courseName}" - ${entry.FACULTY} - ${entry.SLOT}`);
            } else if (entry.CODE === courseCode && !entry.CODE.endsWith('-Lab') && !entry.isLab) {
                matches = true;
                exactMatches++;
            }
        }
        
        if (matches) {
            const slots = parseSlotString(entry.SLOT);

            options.push({
                code: entry.CODE,
                originalCode: entry.ORIGINAL_CODE || entry.CODE,
                title: entry.TITLE,
                faculty: entry.FACULTY,
                slot: entry.SLOT,
                slots: slots,
                venue: entry.VENUE,
                credits: entry.CREDITS,
                type: entry.TYPE,
                isLab: entry.isLab || isLabCourse
            });
        }
    }
    
    console.log(`[getCourseOptions] Found ${options.length} options for "${courseCode}" (exact: ${exactMatches}, name: ${nameMatches}, base: ${baseMatches})`);
    
    return options;
}

/**
 * Forward checking - check if remaining courses can still be scheduled
 */
function canScheduleRemainingCourses(courses, allData, usedSlots, startIndex) {
    for (let i = startIndex; i < courses.length; i++) {
        const options = getCourseOptions(courses[i].code, allData);
        let hasValidOption = false;
        
        for (const option of options) {
            if (!hasConflict(option.slots, usedSlots)) {
                hasValidOption = true;
                break;
            }
        }
        
        if (!hasValidOption) {
            return false; // At least one course cannot be scheduled
        }
    }
    return true;
}

/**
 * Heuristic: Calculate constraint degree (how many conflicts with remaining courses)
 */
function calculateConstraintDegree(course, remainingCourses, allData, usedSlots) {
    let conflicts = 0;
    const options = getCourseOptions(course, allData);
    
    for (const remainingCourse of remainingCourses) {
        const remainingOptions = getCourseOptions(remainingCourse, allData);
        let canConflict = false;
        
        for (const opt of options) {
            const newSlots = [...usedSlots, ...opt.slots];
            for (const remOpt of remainingOptions) {
                if (hasConflict(remOpt.slots, newSlots)) {
                    canConflict = true;
                    break;
                }
            }
            if (canConflict) break;
        }
        
        if (canConflict) conflicts++;
    }
    
    return conflicts;
}

/**
 * MRV + Degree heuristic: Sort courses by minimum remaining values and constraint degree
 */
function sortCoursesByHeuristic(courses, allData, preferences) {
    return courses.map(course => {
        const options = getCourseOptions(course.code, allData);
        
        // Apply teacher preferences filter
        let validOptions = options;
        if (preferences.teacherPreferences && preferences.teacherPreferences[course.code]) {
            const prefs = preferences.teacherPreferences[course.code];
            validOptions = options.filter(opt => {
                if (prefs.red && prefs.red.includes(opt.faculty)) return false;
                return true;
            });
        }
        
        return {
            course: course,
            optionCount: validOptions.length,
            options: validOptions
        };
    }).sort((a, b) => {
        // Sort by: fewer options first (MRV), then by constraint degree
        if (a.optionCount !== b.optionCount) {
            return a.optionCount - b.optionCount;
        }
        // Courses with more options are less constrained
        return 0;
    });
}

/**
 * Enhanced recursive backtracking with forward checking and heuristics
 */
function findValidCombinationEnhanced(courses, allData, preferences, schema, currentIndex = 0, selected = [], usedSlots = [], bestSoFar = null) {
    // Update best solution found so far
    if (selected.length > (bestSoFar?.length || 0)) {
        bestSoFar = [...selected];
    }
    
    // Base case: all courses assigned
    if (currentIndex >= courses.length) {
        return selected;
    }
    
    const currentCourse = courses[currentIndex];
    let options = getCourseOptions(currentCourse.code, allData);
    
    // Filter out RED teachers
    if (preferences.teacherPreferences && preferences.teacherPreferences[currentCourse.code]) {
        const prefs = preferences.teacherPreferences[currentCourse.code];
        if (prefs.red && prefs.red.length > 0) {
            options = options.filter(opt => !prefs.red.includes(opt.faculty));
        }
    }
    
    // Determine if this is a lab course
    const isLab = options.length > 0 && isLabCourse(options[0].slots);
    
    // Sort options by score (best first)
    options.sort((a, b) => {
        const scoreA = calculateScore(
            a.slots, 
            preferences, 
            schema, 
            currentCourse.code, 
            a.faculty, 
            isLab,
            selected
        );
        const scoreB = calculateScore(
            b.slots, 
            preferences, 
            schema, 
            currentCourse.code, 
            b.faculty, 
            isLab,
            selected
        );
        return scoreB - scoreA;
    });
    
    // Try each option
    for (const option of options) {
        // Check for conflicts with already selected courses
        if (!hasConflict(option.slots, usedSlots)) {
            const newUsedSlots = [...usedSlots, ...option.slots];
            
            // Forward checking: verify remaining courses can still be scheduled
            if (!canScheduleRemainingCourses(courses, allData, newUsedSlots, currentIndex + 1)) {
                // Skip this option if it prevents future courses from being scheduled
                // But continue to try other options
                continue;
            }
            
            // Add lab flag to option
            option.isLab = isLab;
            option.code = currentCourse.code;
            
            // This option is valid, recurse
            const result = findValidCombinationEnhanced(
                courses,
                allData,
                preferences,
                schema,
                currentIndex + 1,
                [...selected, option],
                newUsedSlots,
                bestSoFar
            );
            
            if (result !== null && result.length === courses.length) {
                return result; // Found complete solution
            }
            
            // Keep track of best partial solution
            if (result && result.length > (bestSoFar?.length || 0)) {
                bestSoFar = result;
            }
        }
    }
    
    // Return best partial solution if available, otherwise null
    return bestSoFar && bestSoFar.length > 0 ? bestSoFar : null;
}

/**
 * Greedy algorithm - schedule courses one by one, maximizing at each step
 */
function findGreedySolution(courses, allData, preferences, schema) {
    const selected = [];
    const usedSlots = [];
    const sortedCourses = sortCoursesByHeuristic(courses, allData, preferences);
    
    for (const { course, options } of sortedCourses) {
        const isLab = options.length > 0 && isLabCourse(options[0].slots);
        
        // Sort options by score
        const scoredOptions = options.map(opt => ({
            ...opt,
            score: calculateScore(
                opt.slots,
                preferences,
                schema,
                course.code,
                opt.faculty,
                isLab,
                selected
            )
        })).sort((a, b) => b.score - a.score);
        
        // Find first non-conflicting option
        for (const option of scoredOptions) {
            if (!hasConflict(option.slots, usedSlots)) {
                option.isLab = isLab;
                option.code = course.code;
                selected.push(option);
                usedSlots.push(...option.slots);
                break;
            }
        }
    }
    
    return selected.length === courses.length ? selected : selected;
}

/**
 * Advanced constraint satisfaction with multi-strategy optimization
 */
function findMaximalSchedule(courses, allData, preferences, schema) {
    console.log(`🚀 Starting advanced CSP solver for ${courses.length} courses...`);
    
    // Pre-filter options for each course (remove RED teachers, apply preferences)
    const courseOptionsMap = new Map();
    
    for (const course of courses) {
        let options = getCourseOptions(course, allData);
        
        // Filter RED teachers
        if (preferences.teacherPreferences && preferences.teacherPreferences[course.code]) {
            const prefs = preferences.teacherPreferences[course.code];
            if (prefs.red && prefs.red.length > 0) {
                options = options.filter(opt => !prefs.red.includes(opt.faculty));
            }
        }
        
        if (options.length === 0) {
            console.warn(`❌ ${course.code} has no valid options (all RED or unavailable)`);
            continue;
        }
        
        courseOptionsMap.set(course.code, options);
    }
    
    // Build compatibility matrix (which course options are compatible)
    const compatibilityCache = new Map();
    
    function areOptionsCompatible(opt1, opt2) {
        const key = `${opt1.code}_${opt1.slot}_${opt2.code}_${opt2.slot}`;
        if (compatibilityCache.has(key)) return compatibilityCache.get(key);
        
        const compatible = !hasConflict(opt1.slots, opt2.slots);
        compatibilityCache.set(key, compatible);
        return compatible;
    }
    
    // Try multiple strategies
    let bestSolution = null;
    
    // Strategy 1: Greedy with best-first heuristic
    const greedy = findGreedySolution(courses, allData, preferences, schema);
    if (greedy.length > (bestSolution?.length || 0)) {
        bestSolution = greedy;
        console.log(`✅ Greedy found: ${greedy.length} courses`);
    }
    
    // Strategy 2: Enhanced backtracking with forward checking (time-limited)
    const sortedByMRV = sortCoursesByHeuristic(courses, allData, preferences);
    const mrvCourses = sortedByMRV.map(item => item.course);
    
    const backtrackResult = findValidCombinationEnhanced(
        mrvCourses,
        allData,
        preferences,
        schema,
        0,
        [],
        [],
        bestSolution
    );
    
    if (backtrackResult && backtrackResult.length > (bestSolution?.length || 0)) {
        bestSolution = backtrackResult;
        console.log(`✅ Enhanced backtracking found: ${backtrackResult.length} courses`);
    }
    
    // Strategy 3: Try different course orderings (permutation sampling)
    // Use more permutations for larger course sets
    if (bestSolution && bestSolution.length < courses.length) {
        const numPermutations = Math.min(10, Math.max(5, Math.floor(courses.length / 2)));
        console.log(`🔄 Trying ${numPermutations} alternative orderings...`);
        
        for (let attempt = 0; attempt < numPermutations; attempt++) {
            const shuffled = [...courses].sort(() => Math.random() - 0.5);
            const permResult = findValidCombinationEnhanced(
                shuffled,
                allData,
                preferences,
                schema,
                0,
                [],
                [],
                bestSolution
            );
            
            if (permResult && permResult.length > bestSolution.length) {
                bestSolution = permResult;
                console.log(`✅ Permutation ${attempt + 1} found: ${permResult.length} courses`);
                
                if (bestSolution.length === courses.length) {
                    break; // Found complete solution
                }
            }
        }
    }
    
    // Strategy 4: Maximum independent set approach (find largest non-conflicting subset)
    // Always try this if we don't have a complete solution
    if (bestSolution && bestSolution.length < courses.length) {
        console.log(`🔄 Trying maximal independent set approach...`);
        const misResult = findMaximalIndependentSet(courses, allData, preferences, schema);
        
        if (misResult && misResult.length > bestSolution.length) {
            bestSolution = misResult;
            console.log(`✅ MIS approach found: ${misResult.length} courses`);
        }
    }
    
    return bestSolution;
}

/**
 * Maximum independent set - find largest subset of courses that don't conflict
 */
function findMaximalIndependentSet(courses, allData, preferences, schema) {
    // Build graph where edges represent conflicts between course options
    const courseOptions = courses.map(course => {
        let options = getCourseOptions(course, allData);
        
        // Filter RED teachers
        if (preferences.teacherPreferences && preferences.teacherPreferences[course.code]) {
            const prefs = preferences.teacherPreferences[course.code];
            if (prefs.red) {
                options = options.filter(opt => !prefs.red.includes(opt.faculty));
            }
        }
        
        const isLab = options.length > 0 && isLabCourse(options[0].slots);
        
        // Score and sort options
        return options.map(opt => ({
            ...opt,
            course: course,
            isLab: isLab,
            code: course.code,
            score: calculateScore(
                opt.slots,
                preferences,
                schema,
                course.code,
                opt.faculty,
                isLab,
                []
            )
        })).sort((a, b) => b.score - a.score);
    });
    
    // Use dynamic programming to find maximum independent set
    let bestSet = [];
    
    function findBestCombination(courseIdx, selected, usedSlots) {
        if (courseIdx >= courseOptions.length) {
            if (selected.length > bestSet.length) {
                bestSet = [...selected];
            }
            return;
        }
        
        // Try to include current course
        const options = courseOptions[courseIdx];
        let added = false;
        
        for (const option of options) {
            if (!hasConflict(option.slots, usedSlots)) {
                findBestCombination(
                    courseIdx + 1,
                    [...selected, option],
                    [...usedSlots, ...option.slots]
                );
                added = true;
                break; // Only try the best option for each course
            }
        }
        
        // Also try skipping this course
        if (!added) {
            findBestCombination(courseIdx + 1, selected, usedSlots);
        }
    }
    
    findBestCombination(0, [], []);
    return bestSet;
}

/**
 * Hybrid approach with multiple advanced strategies
 */
function findValidCombination(courses, allData, preferences, schema) {
    if (courses.length === 0) return [];
    
    console.log(`🎯 Processing ${courses.length} courses with advanced algorithms...`);
    
    // Use the maximal schedule finder
    const result = findMaximalSchedule(courses, allData, preferences, schema);
    
    if (result && result.length > 0) {
        console.log(`✅ Found solution with ${result.length}/${courses.length} courses`);
        return result;
    }
    
    console.log(`❌ No valid schedule found`);
    return null;
}

/**
 * Get clashing teachers for an L course selection
 * Shows which green/orange teachers clash with the selected lab slot
 */
export function getClashingTeachersForLab(labSlot, courseCode, allData, teacherPreferences) {
    const clashingInfo = {
        green: [],
        orange: [],
        selectedSlot: labSlot
    };
    
    if (!teacherPreferences || !teacherPreferences[courseCode]) {
        return clashingInfo;
    }
    
    const prefs = teacherPreferences[courseCode];
    const allOptions = getCourseOptions(courseCode, allData);
    
    // Check each green teacher
    if (prefs.green) {
        prefs.green.forEach(teacher => {
            const teacherOption = allOptions.find(opt => opt.faculty === teacher);
            if (teacherOption && hasConflict(teacherOption.slots, [labSlot])) {
                clashingInfo.green.push({
                    teacher: teacher,
                    slots: teacherOption.slots,
                    clashingSlots: teacherOption.slots.filter(s => 
                        clashMap[s]?.includes(labSlot) || 
                        clashMap[labSlot]?.includes(s) ||
                        s === labSlot
                    )
                });
            }
        });
    }
    
    // Check each orange teacher
    if (prefs.orange) {
        prefs.orange.forEach(teacher => {
            const teacherOption = allOptions.find(opt => opt.faculty === teacher);
            if (teacherOption && hasConflict(teacherOption.slots, [labSlot])) {
                clashingInfo.orange.push({
                    teacher: teacher,
                    slots: teacherOption.slots,
                    clashingSlots: teacherOption.slots.filter(s => 
                        clashMap[s]?.includes(labSlot) || 
                        clashMap[labSlot]?.includes(s) ||
                        s === labSlot
                    )
                });
            }
        });
    }
    
    return clashingInfo;
}

/**
 * Main function to generate auto-timetable
 * @param {Array} selectedCourses - Array of course objects with {code, title, credits}
 * @param {Array} allData - All available course data
 * @param {Object} preferences - User preferences for optimization
 * @param {Object} schema - Time schema (vellore/chennai)
 * @returns {Object} Result object with success flag and data
 */
export function generateAutoTimetable(selectedCourses, allData, preferences = {}, schema = null) {
    // Merge preferences with defaults
    const prefs = { ...defaultPreferences, ...preferences };
    
    // Validate inputs
    if (!selectedCourses || selectedCourses.length === 0) {
        return {
            success: false,
            error: 'No courses selected. Please add courses first.'
        };
    }
    
    if (!allData || allData.length === 0) {
        return {
            success: false,
            error: 'Course data not loaded. Please refresh the page.'
        };
    }
    
    // Check if all courses have available options
    for (const course of selectedCourses) {
        const options = getCourseOptions(course.code, allData);
        if (options.length === 0) {
            return {
                success: false,
                error: `No available slots found for course: ${course.code} - ${course.title}`
            };
        }
    }
    
    // Try to find valid combination with strict preferences
    let result = findValidCombination(selectedCourses, allData, prefs, schema);
    
    // Handle partial solutions (maximizing number of courses)
    if (result && result.length > 0 && result.length < selectedCourses.length) {
        const scheduledCodes = new Set(result.map(r => r.code));
        const unscheduledCourses = selectedCourses.filter(c => !scheduledCodes.has(c.code));
        
        return {
            success: true,
            data: result,
            partial: true,
            scheduled: result.length,
            total: selectedCourses.length,
            unscheduled: unscheduledCourses.map(c => c.code),
            message: `Successfully scheduled ${result.length} out of ${selectedCourses.length} courses. ` +
                     `Unable to schedule: ${unscheduledCourses.map(c => c.code).join(', ')}. ` +
                     `You can try removing these courses or adjusting preferences.`
        };
    }
    
    if (result === null || result.length === 0) {
        // Try with relaxed teacher preferences (allow red teachers as last resort)
        const relaxedPrefs = { ...prefs };
        const hasRedTeachers = Object.values(prefs.teacherPreferences || {}).some(p => p.red && p.red.length > 0);
        
        if (hasRedTeachers) {
            // Remove red teacher restrictions temporarily
            relaxedPrefs.teacherPreferences = {};
            result = findValidCombination(selectedCourses, allData, relaxedPrefs, schema);
            
            if (result !== null) {
                return {
                    success: false,
                    error: `⚠️ Cannot create timetable with your RED teacher restrictions.\n\n` +
                           `The only way to fit all courses is to include some RED teachers.\n\n` +
                           `Suggestions:\n` +
                           `• Remove some RED teacher restrictions\n` +
                           `• Select different courses\n` +
                           `• Manually choose specific slots\n\n` +
                           `Tip: Try marking fewer teachers as RED, or use ORANGE instead.`
                };
            }
        }
        
        // Try without lab time restrictions
        if (prefs.labsBefore7PM) {
            const noLabRestriction = { ...prefs, labsBefore7PM: false };
            result = findValidCombination(selectedCourses, allData, noLabRestriction, schema);
            
            if (result !== null) {
                return {
                    success: false,
                    error: `⚠️ Cannot schedule all labs before 7 PM.\n\n` +
                           `A valid timetable exists, but requires labs after 7 PM.\n\n` +
                           `Suggestions:\n` +
                           `• Disable "Labs Before 7 PM" option\n` +
                           `• Choose courses with earlier lab slots\n` +
                           `• Reduce number of lab courses\n\n` +
                           `Would you like to try without the 7 PM restriction?`
                };
            }
        }
        
        // Try without early morning restriction
        if (prefs.avoidEarlyMorning) {
            const allowEarlyMorning = { ...prefs, avoidEarlyMorning: false };
            result = findValidCombination(selectedCourses, allData, allowEarlyMorning, schema);
            
            if (result !== null) {
                return {
                    success: false,
                    error: `⚠️ Cannot avoid 8 AM classes with current courses.\n\n` +
                           `A valid timetable exists, but includes 8 AM classes.\n\n` +
                           `Suggestions:\n` +
                           `• Disable "Avoid 8 AM Classes" option\n` +
                           `• Choose different courses with later timings\n` +
                           `• Accept some early morning classes\n\n` +
                           `Would you like to try with 8 AM classes?`
                };
            }
        }
        
        // Analyze why it failed
        const analysis = analyzeTimetableImpossibility(selectedCourses, allData, prefs);
        
        return {
            success: false,
            error: `❌ Could not find a valid timetable without conflicts.\n\n` +
                   `${analysis}\n\n` +
                   `💡 Quick Fixes:\n` +
                   `• Try generating again (sometimes different order works)\n` +
                   `• Remove one course temporarily\n` +
                   `• Relax teacher color restrictions\n` +
                   `• Disable time preferences (8 AM, 7 PM limits)\n` +
                   `• Manually select teachers for conflicting courses`
        };
    }
    
    // Convert result to course data format
    const timetableData = result.map((option, index) => ({
        courseId: index,
        courseCode: option.code,
        courseTitle: option.title,
        faculty: option.faculty,
        slots: option.slots,
        venue: option.venue,
        credits: option.credits,
        isProject: option.type === 'EPJ' ? 'true' : 'false'
    }));
    
    return {
        success: true,
        data: timetableData,
        message: `Successfully generated timetable with ${timetableData.length} courses!`
    };
}

/**
 * Analyze conflicts in current timetable
 * @param {Array} courseData - Current timetable data
 * @returns {Array} List of conflicts found
 */
export function analyzeConflicts(courseData) {
    const conflicts = [];
    
    for (let i = 0; i < courseData.length; i++) {
        for (let j = i + 1; j < courseData.length; j++) {
            if (hasConflict(courseData[i].slots, courseData[j].slots)) {
                conflicts.push({
                    course1: {
                        code: courseData[i].courseCode,
                        title: courseData[i].courseTitle,
                        slots: courseData[i].slots
                    },
                    course2: {
                        code: courseData[j].courseCode,
                        title: courseData[j].courseTitle,
                        slots: courseData[j].slots
                    }
                });
            }
        }
    }
    
    return conflicts;
}

/**
 * Get alternative options for a course that avoid conflicts
 * @param {String} courseCode - Course code to find alternatives for
 * @param {Array} allData - All available course data
 * @param {Array} usedSlots - Currently occupied slots
 * @returns {Array} List of non-conflicting alternatives
 */
export function getAlternativeOptions(courseCode, allData, usedSlots) {
    const options = getCourseOptions(courseCode, allData);
    
    return options.filter(option => !hasConflict(option.slots, usedSlots));
}

/**
 * Analyze why timetable generation failed
 * @param {Array} selectedCourses - Courses that couldn't be scheduled
 * @param {Array} allData - All available course data
 * @param {Object} preferences - User preferences
 * @returns {String} Detailed analysis message
 */
function analyzeTimetableImpossibility(selectedCourses, allData, preferences) {
    let analysis = `📊 Analysis of ${selectedCourses.length} courses:\n\n`;
    
    // Check each course for available options
    const limitedCourses = [];
    const noGreenOptions = [];
    
    selectedCourses.forEach(course => {
        const options = getCourseOptions(course.code, allData);
        
        if (options.length === 0) {
            analysis += `❌ ${course.code}: No available slots found\n`;
            return;
        }
        
        // Check for teacher preference restrictions
        if (preferences.teacherPreferences && preferences.teacherPreferences[course.code]) {
            const prefs = preferences.teacherPreferences[course.code];
            const greenOptions = options.filter(opt => prefs.green && prefs.green.includes(opt.faculty));
            const nonRedOptions = options.filter(opt => !prefs.red || !prefs.red.includes(opt.faculty));
            
            if (greenOptions.length === 0 && prefs.green && prefs.green.length > 0) {
                noGreenOptions.push(course.code);
            }
            
            if (nonRedOptions.length < 3) {
                limitedCourses.push(`${course.code} (${nonRedOptions.length} options)`);
            }
        }
        
        if (options.length < 5) {
            analysis += `⚠️ ${course.code}: Limited to ${options.length} slot options\n`;
        }
    });
    
    if (limitedCourses.length > 0) {
        analysis += `\n🎨 Courses with heavy color restrictions:\n`;
        limitedCourses.forEach(c => analysis += `   • ${c}\n`);
    }
    
    if (noGreenOptions.length > 0) {
        analysis += `\n⚠️ No GREEN teachers available for: ${noGreenOptions.join(', ')}\n`;
    }
    
    // Check for heavily conflicting course pairs
    const conflicts = [];
    for (let i = 0; i < selectedCourses.length; i++) {
        for (let j = i + 1; j < selectedCourses.length; j++) {
            const course1 = selectedCourses[i];
            const course2 = selectedCourses[j];
            const options1 = getCourseOptions(course1.code, allData);
            const options2 = getCourseOptions(course2.code, allData);
            
            // Count non-conflicting combinations
            let validCombos = 0;
            options1.forEach(opt1 => {
                options2.forEach(opt2 => {
                    if (!hasConflict(opt1.slots, opt2.slots)) {
                        validCombos++;
                    }
                });
            });
            
            if (validCombos === 0) {
                conflicts.push(`${course1.code} ↔ ${course2.code}`);
            } else if (validCombos < 5) {
                analysis += `\n⚠️ ${course1.code} and ${course2.code}: Only ${validCombos} non-conflicting combinations\n`;
            }
        }
    }
    
    if (conflicts.length > 0) {
        analysis += `\n❌ These courses ALWAYS conflict:\n`;
        conflicts.forEach(c => analysis += `   • ${c}\n`);
        analysis += `\n⚡ Action Required: Remove one course from each conflicting pair\n`;
    }
    
    return analysis;
}

export { defaultPreferences };

